var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
import {
  Box,
  Vec,
  atom,
  bind,
  clamp,
  computed,
  PI2,
  react,
  uniqueId
} from "@tldraw/editor";
class MinimapManager {
  constructor(editor, elem, container) {
    this.editor = editor;
    this.elem = elem;
    this.container = container;
    const ctx = elem.getContext("2d");
    if (!ctx) throw new Error("Minimap: could not get 2D canvas context");
    this.ctx = ctx;
    this.shapeRectCache = editor.store.createComputedCache(
      "minimap-shape-rect",
      (shape) => {
        const util = editor.getShapeUtil(shape.type);
        if (util.hideInMinimap?.(shape)) return null;
        return editor.getShapeMaskedPageBounds(shape.id) ?? null;
      }
    );
    this.colors = this._getColors();
    this.disposables.push(this._listenForCanvasResize(), react("minimap render", this.render));
  }
  editor;
  elem;
  container;
  disposables = [];
  close() {
    this.disposables.forEach((d) => d());
  }
  ctx;
  shapeRectCache;
  _getColors() {
    const style = this.editor.getContainerWindow().getComputedStyle(this.editor.getContainer());
    return {
      shapeFill: style.getPropertyValue("--tl-color-text-3").trim(),
      selectFill: style.getPropertyValue("--tl-color-selected").trim(),
      viewportFill: style.getPropertyValue("--tl-color-muted-1").trim(),
      background: style.getPropertyValue("--tl-color-low").trim()
    };
  }
  colors;
  // this should be called after dark/light mode changes have propagated to the dom
  updateColors() {
    this.colors = this._getColors();
  }
  id = uniqueId();
  getDpr() {
    return this.editor.getInstanceState().devicePixelRatio;
  }
  getContentPageBounds() {
    const viewportPageBounds = this.editor.getViewportPageBounds();
    const commonShapeBounds = this.editor.getCurrentPageBounds();
    return commonShapeBounds ? Box.Expand(commonShapeBounds, viewportPageBounds) : viewportPageBounds;
  }
  _getCanvasBoundingRect() {
    const { x, y, width, height } = this.elem.getBoundingClientRect();
    return new Box(x, y, width, height);
  }
  canvasBoundingClientRect = atom("canvasBoundingClientRect", new Box());
  getCanvasScreenBounds() {
    return this.canvasBoundingClientRect.get();
  }
  _listenForCanvasResize() {
    const observer = new ResizeObserver(() => {
      const rect = this._getCanvasBoundingRect();
      this.canvasBoundingClientRect.set(rect);
    });
    observer.observe(this.elem);
    observer.observe(this.container);
    return () => observer.disconnect();
  }
  getCanvasSize() {
    const rect = this.canvasBoundingClientRect.get();
    const dpr = this.getDpr();
    return new Vec(rect.width * dpr, rect.height * dpr);
  }
  originPagePoint = new Vec();
  originPageCenter = new Vec();
  isInViewport = false;
  getCanvasPageBounds() {
    const canvasScreenBounds = this.getCanvasScreenBounds();
    const contentPageBounds = this.getContentPageBounds();
    const aspectRatio = canvasScreenBounds.width / canvasScreenBounds.height;
    let targetWidth = contentPageBounds.width;
    let targetHeight = targetWidth / aspectRatio;
    if (targetHeight < contentPageBounds.height) {
      targetHeight = contentPageBounds.height;
      targetWidth = targetHeight * aspectRatio;
    }
    const box = new Box(0, 0, targetWidth, targetHeight);
    box.center = contentPageBounds.center;
    return box;
  }
  getZoom() {
    return this.getCanvasScreenBounds().width / this.getCanvasPageBounds().width;
  }
  getMinimapPagePoint(clientX, clientY) {
    const canvasPageBounds = this.getCanvasPageBounds();
    const canvasScreenBounds = this.getCanvasScreenBounds();
    let x = clientX - canvasScreenBounds.x;
    let y = clientY - canvasScreenBounds.y;
    x *= canvasPageBounds.width / canvasScreenBounds.width;
    y *= canvasPageBounds.height / canvasScreenBounds.height;
    x += canvasPageBounds.minX;
    y += canvasPageBounds.minY;
    return new Vec(x, y, 1);
  }
  minimapScreenPointToPagePoint(x, y, shiftKey = false, clampToBounds = false) {
    const { editor } = this;
    const vpPageBounds = editor.getViewportPageBounds();
    let { x: px, y: py } = this.getMinimapPagePoint(x, y);
    if (clampToBounds) {
      const shapesPageBounds = this.editor.getCurrentPageBounds() ?? new Box();
      const minX = shapesPageBounds.minX - vpPageBounds.width / 2;
      const maxX = shapesPageBounds.maxX + vpPageBounds.width / 2;
      const minY = shapesPageBounds.minY - vpPageBounds.height / 2;
      const maxY = shapesPageBounds.maxY + vpPageBounds.height / 2;
      const lx = Math.max(0, minX + vpPageBounds.width - px);
      const rx = Math.max(0, -(maxX - vpPageBounds.width - px));
      const ly = Math.max(0, minY + vpPageBounds.height - py);
      const ry = Math.max(0, -(maxY - vpPageBounds.height - py));
      px += (lx - rx) / 2;
      py += (ly - ry) / 2;
      px = clamp(px, minX, maxX);
      py = clamp(py, minY, maxY);
    }
    if (shiftKey) {
      const { originPagePoint } = this;
      const dx = Math.abs(px - originPagePoint.x);
      const dy = Math.abs(py - originPagePoint.y);
      if (dx > dy) {
        py = originPagePoint.y;
      } else {
        px = originPagePoint.x;
      }
    }
    return new Vec(px, py);
  }
  getShapePaths() {
    const { editor } = this;
    const selectedIds = new Set(editor.getSelectedShapeIds());
    const ids = editor.getCurrentPageShapeIdsSorted();
    const shapes = new Path2D();
    const selected = new Path2D();
    for (let i = 0, len = ids.length; i < len; i++) {
      const shapeId = ids[i];
      const bounds = this.shapeRectCache.get(shapeId);
      if (!bounds) continue;
      const target = selectedIds.has(shapeId) ? selected : shapes;
      target.rect(bounds.x, bounds.y, bounds.w, bounds.h);
    }
    return { shapes, selected };
  }
  render() {
    const { ctx, editor, elem } = this;
    const canvasSize = this.getCanvasSize();
    const canvasPageBounds = this.getCanvasPageBounds();
    const dpr = this.getDpr();
    const zoom = this.getZoom();
    if (elem.width !== canvasSize.x || elem.height !== canvasSize.y) {
      elem.width = canvasSize.x;
      elem.height = canvasSize.y;
    }
    ctx.resetTransform();
    ctx.fillStyle = this.colors.background;
    ctx.fillRect(0, 0, canvasSize.x, canvasSize.y);
    ctx.scale(dpr * zoom, dpr * zoom);
    ctx.translate(-canvasPageBounds.minX, -canvasPageBounds.minY);
    const { shapes: shapesPath, selected: selectedPath } = this.getShapePaths();
    ctx.fillStyle = this.colors.shapeFill;
    ctx.fill(shapesPath);
    ctx.fillStyle = this.colors.selectFill;
    ctx.fill(selectedPath);
    const viewport = editor.getViewportPageBounds();
    const { minX: vx, minY: vy, width: vw, height: vh } = viewport;
    const r = Math.min(vw / 4, vh / 4, 4 / zoom);
    ctx.beginPath();
    if (r * zoom < 1) {
      ctx.rect(vx, vy, vw, vh);
    } else {
      ctx.roundRect(vx, vy, vw, vh, r);
    }
    ctx.fillStyle = this.colors.viewportFill;
    ctx.fill();
    const entries = editor.overlays.getActiveOverlayEntries();
    for (const { util, overlays } of entries) {
      ctx.save();
      util.renderMinimap(ctx, overlays, zoom);
      ctx.restore();
    }
    const dotRadius = 3 / zoom;
    for (const presence of editor.getVisibleCollaboratorsOnCurrentPage()) {
      if (!presence.cursor) continue;
      ctx.beginPath();
      ctx.arc(presence.cursor.x, presence.cursor.y, dotRadius, 0, PI2);
      ctx.fillStyle = presence.color;
      ctx.fill();
    }
  }
}
__decorateClass([
  bind
], MinimapManager.prototype, "close", 1);
__decorateClass([
  computed
], MinimapManager.prototype, "getDpr", 1);
__decorateClass([
  computed
], MinimapManager.prototype, "getContentPageBounds", 1);
__decorateClass([
  computed
], MinimapManager.prototype, "getCanvasSize", 1);
__decorateClass([
  computed
], MinimapManager.prototype, "getCanvasPageBounds", 1);
__decorateClass([
  computed
], MinimapManager.prototype, "getZoom", 1);
__decorateClass([
  computed
], MinimapManager.prototype, "getShapePaths", 1);
__decorateClass([
  bind
], MinimapManager.prototype, "render", 1);
export {
  MinimapManager
};
//# sourceMappingURL=MinimapManager.mjs.map
